import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

import { Box } from '@mui/material';

import { Composer, Pin, usePageKey } from '@/designs-dev/comments/CommentLayer';
import CommentsPanel from '@/designs-dev/comments/CommentsPanel';
import { ThreadBubble } from '@/designs-dev/comments/Thread';
import {
  addComment,
  commentsSnapshot,
  contentOf,
  isResolved,
  isUnread,
  selectThread,
  startDraft,
  threadsOf,
  useComments,
  watchPage,
} from '@/designs-dev/comments/store';
import { CHROME_VH, CHROME_ZOOM } from '@/designs-dev/inspect/chrome';

// Comments on a flows or spec page. These pages are a canvas you pan and zoom
// rather than one screen, so a comment is pinned to a point on the canvas,
// in its own unzoomed pixels, rather than to a component. The pins live in the
// canvas's moving layer, so they pan and zoom with the page for free.
export const CANVAS_ANCHOR = 'canvas';

const typing = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

const CanvasComments = ({
  layer,
  zoom,
  commenting,
  setCommenting,
  focus,
}: {
  // The canvas's transformed layer: what pins are placed in and clicks read.
  layer: HTMLElement | null;
  zoom: number;
  commenting: boolean;
  setCommenting: (on: boolean) => void;
  // Brings a point of the canvas into the middle of the view.
  focus: (x: number, y: number) => void;
}) => {
  const page = usePageKey();
  const store = useComments();
  const { comments, selected, draft, listOpen } = store;
  const { user } = useKindeAuth();
  // Where the click that started the draft was, in window space, for the
  // composer.
  const [draftAt, setDraftAt] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    // Loaded whatever the mode, so the bar can say how many there are before
    // comment mode is on.
    return watchPage(page);
  }, [page]);

  // Leaving comment mode puts away whatever was open in it.
  useEffect(() => {
    if (commenting) return;
    startDraft(null);
    selectThread(null);
  }, [commenting]);

  // C toggles comment mode, as on a prototype and in Figma.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'c' || e.metaKey || e.ctrlKey || e.altKey)
        return;
      if (typing(e.target)) return;
      e.preventDefault();
      setCommenting(!commenting);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commenting, setCommenting]);

  useEffect(() => {
    if (!commenting || !layer) return;

    const onClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-drill-handle]')) return;
      e.preventDefault();
      e.stopPropagation();
      // The layer is scaled by a transform, which its offsetWidth ignores, so
      // the two together give the zoom without asking the canvas.
      const r = layer.getBoundingClientRect();
      const z = layer.offsetWidth ? r.width / layer.offsetWidth : 1;
      startDraft({
        anchor: CANVAS_ANCHOR,
        x: (e.clientX - r.left) / z,
        y: (e.clientY - r.top) / z,
      });
      setDraftAt({ x: e.clientX, y: e.clientY });
    };
    // Esc backs out a step at a time: the draft, the open thread, then the mode.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const { draft: d, selected: s } = commentsSnapshot();
      if (d) startDraft(null);
      else if (s) selectThread(null);
      else setCommenting(false);
    };

    // Drill mode's flag: a crosshair everywhere and no press effects, so the
    // minis read as something you point at rather than use.
    layer.dataset.drill = 'on';
    layer.dataset.comment = 'on';
    layer.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKey);
    return () => {
      delete layer.dataset.drill;
      delete layer.dataset.comment;
      layer.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [commenting, layer, setCommenting]);

  if (!commenting) return null;

  const threads = threadsOf(comments);

  return (
    <>
      {layer &&
        createPortal(
          <>
            {threads.map((t, i) => {
              const { x, y } = contentOf(t);
              if (t.anchor !== CANVAS_ANCHOR || x === undefined) return null;
              // Put away once resolved, unless it's the one open.
              if (isResolved(comments, t.id) && selected !== t.id) return null;
              return (
                <Pin
                  key={t.id}
                  n={i + 1}
                  selected={selected === t.id}
                  id={t.id}
                  unread={isUnread(store, t, user?.id)}
                  onClick={() => selectThread(selected === t.id ? null : t.id)}
                  top={y ?? 0}
                  left={x}
                  zoom={zoom}
                />
              );
            })}
            {draft && (
              <Pin n={null} selected top={draft.y} left={draft.x} zoom={zoom} />
            )}
          </>,
          layer,
        )}
      {draft && draftAt && (
        <Composer
          left={draftAt.x}
          top={draftAt.y}
          onCancel={() => startDraft(null)}
          onSubmit={async (text) => {
            const created = await addComment(draft.anchor, {
              text,
              x: draft.x,
              y: draft.y,
            });
            if (created) selectThread(created.id);
          }}
        />
      )}
      <ThreadBubble />
      {listOpen && (
        <Box
          sx={{
            width: 400,
            flexShrink: 0,
            zoom: CHROME_ZOOM,
            height: CHROME_VH(100),
            bgcolor: 'background.paper',
            borderLeft: 1,
            borderColor: 'custom.neutral.200',
            p: 3,
            overflowY: 'auto',
          }}
        >
          <CommentsPanel
            placedOf={(anchors) =>
              new Set(anchors.filter((a) => a === CANVAS_ANCHOR))
            }
            onOpen={(t) => {
              const { x, y } = contentOf(t);
              if (t.anchor === CANVAS_ANCHOR && x !== undefined)
                focus(x, y ?? 0);
            }}
          />
        </Box>
      )}
    </>
  );
};

export default CanvasComments;
